import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { Input, WarningText, Button } from 'components';
import {
  updateUserInfoField,
  resetUserInfoPreserveField,
  userInfoSelector,
  isUserCompanySelector,
  clearUserInfoFields,
  updateIsUserExistField,
  isUserExistSelector,
} from 'redux/slices/signUp/signUpSlice';
import { REGEX_SPECIAL, REGEX_DIGIT, REGEX_CASE, REGEX_EMAIL } from 'constant/Validation';
import { i18n } from 'constant/String';
import { useGetCheckIsUserExistQuery, useCheckTcIdentityMutation, useCheckVknMutation } from 'redux/slices/signUp/SignUpApi';
import { objectToQuery } from 'utils/helper';
import { apiResHandler } from 'utils/axiosBaseQuery';
import { addAlertQueue } from 'redux/slices/snackbar/snackbarSlice';
import ActionButtons from '../ActionButtons/ActionButtons';

const UserInfoForm = ({ onPrev }) => {
  const dispatch = useDispatch();
  const userInfo = useSelector(userInfoSelector);
  const isUserCompany = useSelector(isUserCompanySelector);
  const isUserExist = useSelector(isUserExistSelector);

  const [formErrors, setFormErrors] = useState({});
  const [pwRegex, setPwRegex] = useState({
    special: '',
    min: 'a',
    case: '',
    digit: '',
  });
  const [isTcVerified, setIsTcVerified] = useState(false);
  const [isTcVerificationFailed, setIsTcVerificationFailed] = useState(false);
  const [checkTcIdentity, { isLoading: tcCheckLoading }] = useCheckTcIdentityMutation();

  const [isVknVerified, setIsVknVerified] = useState(false);
  const [isVknVerificationFailed, setIsVknVerificationFailed] = useState(false);
  const [checkVkn, { isLoading: vknCheckLoading }] = useCheckVknMutation();

  const { formState, setValue, setError, clearErrors } = useFormContext();
  const { errors } = formState;

  useEffect(() => {
    const nextErrors = { ...errors };
    if (userInfo?.email && REGEX_EMAIL.test(userInfo.email)) {
      delete nextErrors.email;
    }
    setFormErrors(nextErrors);
  }, [errors, userInfo?.email]);

  const queryString = isUserCompany
    ? objectToQuery({
        taxnum: userInfo?.taxNumber,
      })
    : objectToQuery({
        idnum: userInfo?.identityNumber,
      });

  const { data: isUserExistData } = useGetCheckIsUserExistQuery(
    {
      queries: queryString,
    },
    {
      skip: !userInfo?.taxNumber && !userInfo?.identityNumber,
    }
  );

  useEffect(() => {
    if (isUserExistData) {
      dispatch(updateIsUserExistField(isUserExistData.data));
    }
  }, [isUserExistData]);

  useEffect(() => {
    setValue('identityNumber', userInfo?.identityNumber);
    setValue('taxNumber', userInfo?.taxNumber);
    setValue('name', userInfo?.name);
    setValue('surname', userInfo?.surname);
    setValue('fullName', userInfo?.fullName);
    setValue('email', userInfo?.email);
    setValue('username', userInfo?.username);
    setValue('password', userInfo?.password);
    setValue('password2', userInfo?.password2);
  }, [
    userInfo?.identityNumber,
    userInfo?.taxNumber,
    userInfo?.name,
    userInfo?.surname,
    userInfo?.fullName,
    userInfo?.email,
    userInfo?.username,
    userInfo?.password,
    userInfo?.password2,
  ]);

  const handleChangeUserInfo = (field, value) => {
    dispatch(updateUserInfoField({ field, value }));
    if (field === 'email') {
      setValue('email', value);
      if (value && !REGEX_EMAIL.test(value)) {
        setError('email', { type: 'manual', message: i18n.errors.email });
        setFormErrors((prev) => ({
          ...prev,
          email: { message: i18n.errors.email },
        }));
      } else {
        clearErrors('email');
        setFormErrors((prev) => {
          const next = { ...prev };
          delete next.email;
          return next;
        });
      }
      return;
    }
    if (field === 'identityNumber') {
      setIsTcVerified(false);
      setIsTcVerificationFailed(false);
      setFormErrors({});
      dispatch(resetUserInfoPreserveField({ field: 'identityNumber', value }));
      dispatch(updateIsUserExistField(false));
      return;
    }
    if (field === 'taxNumber') {
      setIsVknVerified(false);
      setIsVknVerificationFailed(false);
      setFormErrors({});
      dispatch(resetUserInfoPreserveField({ field: 'taxNumber', value }));
      dispatch(updateIsUserExistField(false));
      return;
    }
  };

  const handlePasswordRegex = (pw) => {
    setPwRegex({
      ...pwRegex,
      digit: REGEX_DIGIT.test(pw) ? 'success' : 'error',
      special: REGEX_SPECIAL.test(pw) ? 'success' : 'error',
      case: REGEX_CASE.test(pw) ? 'success' : 'error',
      min: pw.length > 7 ? 'success' : 'error',
    });
  };

  const handleOnPrev = () => {
    dispatch(clearUserInfoFields());
    onPrev();
  };

  useEffect(
    () => handlePasswordRegex(userInfo?.password),
    [userInfo?.password]
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter') {
        const activeElement = document.activeElement;
        const identityInput = document.getElementById('identityNumber');
        const taxNumberInput = document.getElementById('taxNumber');

        if (activeElement === identityInput) {
          event.preventDefault();
          if (String(userInfo?.identityNumber || '').length < 11) {
            if (String(userInfo?.identityNumber || '').length >= 1) {
              handleIdentityCheck('tc');
            }
          } else if (String(userInfo?.identityNumber || '').length === 11) {
            if (!isTcVerified) {
              handleIdentityCheck('tc');
            }
          }
        }

        if (activeElement === taxNumberInput) {
          event.preventDefault();
          if (String(userInfo?.taxNumber || '').length < 10) {
            if (String(userInfo?.taxNumber || '').length >= 1) {
              handleIdentityCheck('vkn');
            }
          } else if (String(userInfo?.taxNumber || '').length === 10) {
            if (!isVknVerified) {
              handleIdentityCheck('vkn');
            }
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isTcVerified, userInfo?.identityNumber, isVknVerified, userInfo?.taxNumber]);

  const handleIdentityCheck = (type) => {
    const isTc = type === 'tc';
    const value = isTc ? userInfo?.identityNumber : userInfo?.taxNumber;
    const length = isTc ? 11 : 10;
    const fieldName = isTc ? 'TC kimlik numarası' : 'VKN';

    if (!value || value.length !== length) {
      dispatch(
        addAlertQueue({
          message: `Geçerli bir ${fieldName} giriniz (${length} haneli)`,
          status: 'error',
          show: true,
        })
      );
      return;
    }

    const setVerificationFailed = isTc ? setIsTcVerificationFailed : setIsVknVerificationFailed;
    const setVerified = isTc ? setIsTcVerified : setIsVknVerified;

    setVerificationFailed(false);

    const apiCall = isTc
      ? checkTcIdentity({ kn: value })
      : checkVkn({ vkn: value });

    apiResHandler(
      apiCall,
      () => {
        setVerified(true);
        setVerificationFailed(false);
        dispatch(
          addAlertQueue({
            message: `${fieldName} doğrulandı`,
            status: 'success',
            show: true,
          })
        );
      },
      () => {
        setVerified(false);
        setVerificationFailed(true);
        const errorMessage = `${fieldName} doğrulanamadı`;
        dispatch(
          addAlertQueue({
            message: errorMessage,
            status: 'error',
            show: true,
          })
        );
      }
    );
  };




  return (
    <>
      <div className="flex  w-full overflow-y-auto sm:h-80vh sm:gap-x-4">
        <div className="flex flex-col w-full gap-y-4">
          <div className="md:gap-x-4 flex flex-col md:flex-row">
            {isUserCompany ? (
              <div className="w-full md:w-1/2 flex gap-2">
                <div className="flex-1">
                  <Input
                    required
                    id="taxNumber"
                    name="taxNumber"
                    label="VKN Numarası"
                    value={userInfo?.taxNumber}
                    onChange={(e) => {
                      const next = (e.target.value || '').replace(/[^0-9]/g, '').slice(0, 10);
                      handleChangeUserInfo(e.target.name, next);
                    }}
                    errors={formErrors?.taxNumber?.message}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={10}
                    className={`${isVknVerified ? 'border-green-500' : isVknVerificationFailed ? 'border-red-500' : ''}`}
                    style={{
                      backgroundColor: isVknVerified ? '#dcfce7' : isVknVerificationFailed ? '#fef2f2' : '',
                      borderColor: isVknVerified ? '#22c55e' : isVknVerificationFailed ? '#ef4444' : ''
                    }}
                  />
                </div>
                <div className="flex-shrink-0 mt-auto">
                  <Button
                    text={vknCheckLoading ? "..." : isVknVerified ? "✓" : isVknVerificationFailed ? "✗" : "Doğrula"}
                    onClick={() => handleIdentityCheck('vkn')}
                    disabled={vknCheckLoading || isVknVerified || isVknVerificationFailed || (String(userInfo?.taxNumber || '').length !== 10)}
                    className={`h-10 w-32 px-3 text-xs ${vknCheckLoading ? 'bg-gray-400' : isVknVerificationFailed ? 'bg-red-400' : isVknVerified ? 'bg-green-400' : 'bg-primary'}`}
                    textVariant="white"
                  />
                </div>
              </div>
            ) : (
              <div className="w-full md:w-1/2 flex gap-2">
                <div className="flex-1">
                  <Input
                    required
                    id="identityNumber"
                    name="identityNumber"
                    label="TC Kimlik Numarası"
                    value={userInfo.identityNumber}
                    onChange={(e) => {
                      const next = (e.target.value || '').replace(/[^0-9]/g, '').slice(0, 11);
                      handleChangeUserInfo(e.target.name, next);
                    }}
                    errors={formErrors?.identityNumber?.message}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={11}
                    className={`${isTcVerified ? 'border-green-500' : isTcVerificationFailed ? 'border-red-500' : ''}`}
                    style={{
                      backgroundColor: isTcVerified ? '#dcfce7' : isTcVerificationFailed ? '#fef2f2' : '',
                      borderColor: isTcVerified ? '#22c55e' : isTcVerificationFailed ? '#ef4444' : ''
                    }}
                  />
                </div>
                <div className="flex-shrink-0 mt-auto">
                  <Button
                    text={tcCheckLoading ? "..." : isTcVerified ? "✓" : isTcVerificationFailed ? "✗" : "Doğrula"}
                    onClick={() => handleIdentityCheck('tc')}
                    disabled={tcCheckLoading || isTcVerified || isTcVerificationFailed || (String(userInfo?.identityNumber || '').length !== 11)}
                    className={`h-10 w-32 px-3 text-xs ${tcCheckLoading ? 'bg-gray-400' : isTcVerificationFailed ? 'bg-red-400' : isTcVerified ? 'bg-green-400' : 'bg-primary'}`}
                    textVariant="white"
                  />
                </div>
              </div>
            )}

            <div className="w-full md:w-1/2">
              <Input
                required
                name="email"
                id="email"
                label="E-posta"
                placeholder="xxxxxx@xxx.xxx"
                value={userInfo.email}
                onChange={(e) =>
                  handleChangeUserInfo(e.target.name, e.target.value.replace(/\s/g, ''))
                }
                errors={
                  userInfo.email && REGEX_EMAIL.test(userInfo.email)
                    ? ''
                    : formErrors?.email?.message
                }
                disabled={(!isTcVerified && !isUserCompany) || (isUserCompany && !isVknVerified)}
                className={(!isTcVerified && !isUserCompany) || (isUserCompany && !isVknVerified) ? 'opacity-50' : ''}
              />
            </div>
          </div>
          <div className=" md:gap-x-4 flex flex-col md:flex-row">
            {isUserCompany ? (
              <div className="w-full md:w-1/2">
                <Input
                  maxLength={200}
                  required
                  label="Ad Soyad / Ünvan"
                  name="fullName"
                  id="fullName"
                  value={userInfo.fullName}
                  onChange={(e) =>
                    handleChangeUserInfo(e.target.name, e.target.value)
                  }
                  errors={formErrors?.fullName?.message}
                  disabled={(!isTcVerified && !isUserCompany) || (isUserCompany && !isVknVerified)}
                  className={isUserCompany && !isVknVerified ? 'opacity-50' : ''}
                />
              </div>
            ) : (
              <>
                <div className="w-full md:w-1/4">
                  <Input
                    maxLength={100}
                    required
                    label="Ad"
                    name="name"
                    id="name"
                    value={userInfo.name}
                    onChange={(e) =>
                      handleChangeUserInfo(e.target.name, e.target.value)
                    }
                    errors={formErrors?.name?.message}
                    disabled={(!isTcVerified && !isUserCompany) || (isUserCompany && !isVknVerified)}
                    className={!isTcVerified && !isUserCompany ? 'opacity-50' : ''}
                  />
                </div>
                <div className="w-full md:w-1/4">
                  <Input
                    maxLength={100}
                    required
                    label="Soyad"
                    name="surname"
                    id="surname"
                    value={userInfo.surname}
                    onChange={(e) =>
                      handleChangeUserInfo(e.target.name, e.target.value)
                    }
                    errors={formErrors?.surname?.message}
                    disabled={(!isTcVerified && !isUserCompany) || (isUserCompany && !isVknVerified)}
                    className={!isTcVerified && !isUserCompany ? 'opacity-50' : ''}
                  />
                </div>
              </>
            )}
            <div className="w-full md:w-1/2">
              <Input
                required
                name="username"
                id="username"
                label="Kullanıcı Adı"
                value={userInfo.username}
                onChange={(e) =>
                  handleChangeUserInfo(
                    e.target.name,
                    e.target.value.replace(/\s/g, '').toLocaleLowerCase('TR')
                  )
                }
                errors={formErrors?.username?.message}
                disabled={(!isTcVerified && !isUserCompany) || (isUserCompany && !isVknVerified)}
                className={(!isTcVerified && !isUserCompany) || (isUserCompany && !isVknVerified) ? 'opacity-50' : ''}
              />
            </div>
          </div>
          <div className="flex flex-col md:gap-x-4  md:flex-row">
            <Input
              maxLength={64}
              isTextVisible={false}
              required
              label="Şifre"
              name="password"
              id="password"
              type="password"
              value={userInfo.password}
              onChange={(e) =>
                handleChangeUserInfo(e.target.name, e.target.value)
              }
              errors={formErrors?.password?.message}
              disabled={(!isTcVerified && !isUserCompany) || (isUserCompany && !isVknVerified)}
              className={(!isTcVerified && !isUserCompany) || (isUserCompany && !isVknVerified) ? 'opacity-50' : ''}
            />
            <Input
              maxLength={64}
              isTextVisible={false}
              required
              label="Şifreni Onayla"
              name="password2"
              id="password2"
              type="password"
              value={userInfo.password2}
              onChange={(e) =>
                handleChangeUserInfo(e.target.name, e.target.value)
              }
              errors={formErrors?.password2?.message}
              disabled={(!isTcVerified && !isUserCompany) || (isUserCompany && !isVknVerified)}
              className={(!isTcVerified && !isUserCompany) || (isUserCompany && !isVknVerified) ? 'opacity-50' : ''}
            />
          </div>
          <div className={(!isTcVerified && !isUserCompany) || (isUserCompany && !isVknVerified) ? 'opacity-50' : ''}>
            <WarningText
              text="En az 8(sekiz) karakterli olmalıdır."
              textType={pwRegex.min}
            />
            <WarningText
              text="İçerisinde en az 1(bir) tane büyük ve en az 1(bir) tane küçük harf bulunmalıdır. "
              textType={pwRegex.case}
            />
            <WarningText
              text="İçerisinde en az 1(bir) tane rakam bulunmalıdır."
              textType={pwRegex.digit}
            />
            <WarningText
              text="İçerisinde en az 1(bir) tane özel karakter bulunmalıdır. (@, !,?,A,+,$,#,&,/,{,*,-,],=,...)"
              textType={pwRegex.special}
            />
          </div>
          <div className="w-full flex justify-end mt-10">
            <ActionButtons onPrev={handleOnPrev} isUSerExist={isUserExist} />
          </div>
        </div>
    </div>
    </>
  );
};

UserInfoForm.propTypes = {
  onPrev: PropTypes.func,
};

export default UserInfoForm;
